import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import ajusteInflacionService, {
  type AjusteInflacionAlerta,
  type AjusteInflacionCuentaPendiente,
  type AjusteInflacionLinea,
  type AjusteInflacionProceso,
  type AjusteInflacionResumen,
  type IndiceInflacion,
  type IndiceInflacionFila,
} from '../../services/ajusteInflacionService';
import { getApiErrorMessage } from '../../utils/apiError';
import IndiceIpcAyudaDialog from './IndiceIpcAyudaDialog';

const money = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const coef = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 4, maximumFractionDigits: 6 });

type ModoIndice = 'nivel' | 'variacion';

const CLASES_AJUSTE = [
  { value: 'M', label: 'Monetaria (no ajusta)' },
  { value: 'N', label: 'No monetaria' },
  { value: 'P', label: 'Patrimonio' },
  { value: 'R', label: 'Resultado' },
  { value: 'X', label: 'No ajusta' },
] as const;

function toInputDate(value?: string | null): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function formatFecha(value?: string | null): string {
  const iso = toInputDate(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function erroresDe(error: unknown): string[] {
  const data = (error as { response?: { data?: { errores?: unknown; message?: string } } })?.response?.data;
  const crudos = data?.errores;
  if (Array.isArray(crudos)) {
    const lista = crudos.map((e) => String(e)).filter(Boolean);
    if (lista.length > 0) return lista;
  }
  return [getApiErrorMessage(error, 'No se pudo completar la operación')];
}

function normalizarClave(valor: string): string {
  return valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function numeroCelda(valor: unknown): number | null {
  if (typeof valor === 'number' && Number.isFinite(valor)) return valor;
  if (typeof valor === 'string') {
    const limpio = valor.trim().replace(/\s/g, '').replace(',', '.');
    if (!limpio) return null;
    const n = Number(limpio);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function enteroCelda(valor: unknown): number | null {
  const n = numeroCelda(valor);
  if (n == null || Math.abs(n - Math.round(n)) > 0.000001) return null;
  return Math.round(n);
}

function interpretarPeriodo(valor: unknown): { anio: number; mes: number } | null {
  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    return { anio: valor.getUTCFullYear(), mes: valor.getUTCMonth() + 1 };
  }
  if (typeof valor === 'number' && valor > 20000) {
    const fecha = XLSX.SSF.parse_date_code(valor);
    if (fecha) return { anio: fecha.y, mes: fecha.m };
  }
  const texto = String(valor ?? '').trim();
  const iso = texto.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/);
  if (iso) return { anio: Number(iso[1]), mes: Number(iso[2]) };
  const dmy = texto.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) return { anio: Number(dmy[3]), mes: Number(dmy[2]) };
  const my = texto.match(/^(\d{1,2})[-/](\d{4})$/);
  if (my) return { anio: Number(my[2]), mes: Number(my[1]) };
  return null;
}

function parsearIndicesExcel(matrix: unknown[][]): { filas: IndiceInflacionFila[]; errores: string[] } {
  const errores: string[] = [];
  const primera = matrix.findIndex((fila) => fila.some((celda) => celda != null && String(celda).trim() !== ''));
  if (primera < 0) return { filas: [], errores: ['El archivo no tiene filas.'] };

  const encabezado = matrix[primera].map((celda) => normalizarClave(String(celda ?? '')));
  const clavesAnio = new Set(['anio', 'ano', 'year']);
  const clavesMes = new Set(['mes', 'month']);
  const clavesValor = new Set(['indice', 'ipc', 'valor', 'nivel', 'nivelipc']);
  const clavesVariacion = new Set(['variacion', 'variacionpct', 'variacionporcentual', 'porcentaje', 'pct', 'var']);
  const clavesPeriodo = new Set(['periodo', 'fecha', 'date']);
  const tieneEncabezado = encabezado.some((clave) =>
    clavesAnio.has(clave)
    || clavesMes.has(clave)
    || clavesValor.has(clave)
    || clavesVariacion.has(clave)
    || clavesPeriodo.has(clave));
  const colAnio = tieneEncabezado ? encabezado.findIndex((clave) => clavesAnio.has(clave)) : 0;
  const colMes = tieneEncabezado ? encabezado.findIndex((clave) => clavesMes.has(clave)) : 1;
  const colValor = tieneEncabezado ? encabezado.findIndex((clave) => clavesValor.has(clave)) : 2;
  const colVariacion = tieneEncabezado ? encabezado.findIndex((clave) => clavesVariacion.has(clave)) : -1;
  const colPeriodo = tieneEncabezado ? encabezado.findIndex((clave) => clavesPeriodo.has(clave)) : -1;

  if (tieneEncabezado && colValor < 0 && colVariacion < 0) {
    return { filas: [], errores: ['Falta la columna Índice o Variación %.'] };
  }
  if (tieneEncabezado && colPeriodo < 0 && (colAnio < 0 || colMes < 0)) {
    return { filas: [], errores: ['Indicá las columnas Año y Mes, o una columna Período, más Índice o Variación %.'] };
  }

  const filas: IndiceInflacionFila[] = [];
  const vistos = new Set<string>();
  const inicio = tieneEncabezado ? primera + 1 : primera;
  for (let i = inicio; i < matrix.length; i++) {
    const row = matrix[i] ?? [];
    if (row.every((celda) => celda == null || String(celda).trim() === '')) continue;
    const nroFila = i + 1;
    let anio: number | null;
    let mes: number | null;
    if (colPeriodo >= 0) {
      const periodo = interpretarPeriodo(row[colPeriodo]);
      anio = periodo?.anio ?? null;
      mes = periodo?.mes ?? null;
      if (!periodo) {
        errores.push(`Fila ${nroFila}: el período no es una fecha válida.`);
        continue;
      }
    } else {
      anio = enteroCelda(row[colAnio]);
      mes = enteroCelda(row[colMes]);
    }
    const valor = colValor >= 0 ? numeroCelda(row[colValor]) : null;
    const variacion = colVariacion >= 0 ? numeroCelda(row[colVariacion]) : null;
    if (anio == null || anio < 1900 || anio > 2100) {
      errores.push(`Fila ${nroFila}: el año no es válido.`);
      continue;
    }
    if (mes == null || mes < 1 || mes > 12) {
      errores.push(`Fila ${nroFila}: el mes debe estar entre 1 y 12.`);
      continue;
    }
    const tieneNivel = valor != null && valor > 0;
    const tieneVariacion = variacion != null;
    if (!tieneNivel && !tieneVariacion) {
      errores.push(`Fila ${nroFila}: indicá el Índice (nivel) o la Variación %.`);
      continue;
    }
    const clave = `${anio}-${mes}`;
    if (vistos.has(clave)) {
      errores.push(`Fila ${nroFila}: ${String(mes).padStart(2, '0')}/${anio} está repetido en el archivo.`);
      continue;
    }
    vistos.add(clave);
    filas.push({
      anio,
      mes,
      valor: tieneNivel ? valor : null,
      variacion: tieneNivel ? null : variacion,
    });
  }

  if (filas.length === 0 && errores.length === 0) errores.push('El archivo no tiene índices.');
  if (filas.length > 1200) errores.push('El archivo supera los 1200 meses.');
  return { filas: errores.length > 0 ? [] : filas, errores };
}

function textoRecpam(importe: number): string {
  if (importe < 0) return `RECPAM al haber ${money.format(Math.abs(importe))}`;
  if (importe > 0) return `RECPAM al debe ${money.format(importe)}`;
  return 'RECPAM 0,00';
}

const AjusteInflacion: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const [indices, setIndices] = useState<IndiceInflacion[]>([]);
  const [cargandoIndices, setCargandoIndices] = useState(false);
  const [anioIndice, setAnioIndice] = useState(String(new Date().getFullYear()));
  const [mesIndice, setMesIndice] = useState(String(new Date().getMonth() + 1));
  const [valorIndice, setValorIndice] = useState('');
  const [modoIndice, setModoIndice] = useState<ModoIndice>('nivel');
  const [guardandoIndice, setGuardandoIndice] = useState(false);
  const [importando, setImportando] = useState(false);
  const [erroresImportacion, setErroresImportacion] = useState<string[]>([]);
  const [openImportar, setOpenImportar] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [ayudaIndiceOpen, setAyudaIndiceOpen] = useState(false);

  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [anioReferencia, setAnioReferencia] = useState('');
  const [mesReferencia, setMesReferencia] = useState('');
  const [cuentaRecpam, setCuentaRecpam] = useState<string>('');
  const [proceso, setProceso] = useState<AjusteInflacionProceso | null>(null);
  const [errores, setErrores] = useState<string[]>([]);
  const [calculando, setCalculando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [historial, setHistorial] = useState<AjusteInflacionResumen[]>([]);
  const [alertasPrerreq, setAlertasPrerreq] = useState<AjusteInflacionAlerta[]>([]);
  const [pendientes, setPendientes] = useState<AjusteInflacionCuentaPendiente[]>([]);
  const [cargandoPendientes, setCargandoPendientes] = useState(false);
  const [seleccionPendientes, setSeleccionPendientes] = useState<number[]>([]);
  const [claseMasiva, setClaseMasiva] = useState('N');
  const [clasificandoMasivo, setClasificandoMasivo] = useState(false);
  const [filtroNroCuenta, setFiltroNroCuenta] = useState('');
  const [filtroNombreCuenta, setFiltroNombreCuenta] = useState('');
  const totalPrerrequisitos = alertasPrerreq.length + pendientes.length;

  const cargarIndices = useCallback(async () => {
    try {
      setCargandoIndices(true);
      setIndices(await ajusteInflacionService.getIndices());
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron leer los índices'), { variant: 'error' });
    } finally {
      setCargandoIndices(false);
    }
  }, [enqueueSnackbar]);

  const cargarHistorial = useCallback(async () => {
    try {
      setHistorial(await ajusteInflacionService.historial());
    } catch {
      setHistorial([]);
    }
  }, []);

  const cargarPrerrequisitos = useCallback(async (desde: string, hasta: string, anioRef: string, mesRef: string) => {
    if (!desde || !hasta) {
      setAlertasPrerreq([]);
      setPendientes([]);
      setSeleccionPendientes([]);
      return;
    }
    try {
      setCargandoPendientes(true);
      const data = await ajusteInflacionService.prerrequisitos({
        fechaDesde: desde,
        fechaHasta: hasta,
        anioReferencia: anioRef ? Number(anioRef) : null,
        mesReferencia: mesRef ? Number(mesRef) : null,
      });
      setAlertasPrerreq(data.alertas);
      setPendientes(data.cuentas);
      setSeleccionPendientes((prev) =>
        prev.filter((nro) => data.cuentas.some((c) => c.nroCuenta === nro && c.permiteClasificar))
      );
    } catch {
      setAlertasPrerreq([]);
      setPendientes([]);
      setSeleccionPendientes([]);
    } finally {
      setCargandoPendientes(false);
    }
  }, []);

  useEffect(() => {
    void cargarIndices();
    void cargarHistorial();
    void (async () => {
      try {
        const ejercicio = await ajusteInflacionService.getEjercicio();
        if (ejercicio.fechaDesde) setFechaDesde(toInputDate(ejercicio.fechaDesde));
        if (ejercicio.fechaHasta) setFechaHasta(toInputDate(ejercicio.fechaHasta));
        if (ejercicio.anioReferencia) setAnioReferencia(String(ejercicio.anioReferencia));
        if (ejercicio.mesReferencia) setMesReferencia(String(ejercicio.mesReferencia));
        if (ejercicio.cuentaRecpam) {
          const nombre = (ejercicio.nomCuentaRecpam || '').trim();
          setCuentaRecpam(nombre ? `${ejercicio.cuentaRecpam} ${nombre}` : String(ejercicio.cuentaRecpam));
        }
      } catch {
        /* el usuario puede cargar las fechas a mano */
      }
    })();
  }, [cargarHistorial, cargarIndices]);

  useEffect(() => {
    void cargarPrerrequisitos(fechaDesde, fechaHasta, anioReferencia, mesReferencia);
  }, [fechaDesde, fechaHasta, anioReferencia, mesReferencia, cargarPrerrequisitos]);

  const guardarIndice = async () => {
    const anio = Number(anioIndice);
    const mes = Number(mesIndice);
    const numero = Number(String(valorIndice).replace(',', '.'));
    if (!anio || mes < 1 || mes > 12 || !Number.isFinite(numero)) {
      enqueueSnackbar(
        modoIndice === 'nivel'
          ? 'Completá año, mes y el nivel del IPC'
          : 'Completá año, mes y la variación % del mes',
        { variant: 'warning' }
      );
      return;
    }
    if (modoIndice === 'nivel' && !(numero > 0)) {
      enqueueSnackbar('El nivel del IPC debe ser mayor a cero', { variant: 'warning' });
      return;
    }
    if (modoIndice === 'variacion' && numero <= -100) {
      enqueueSnackbar('La variación no puede ser -100 % o menor', { variant: 'warning' });
      return;
    }
    try {
      setGuardandoIndice(true);
      await ajusteInflacionService.guardarIndice(
        modoIndice === 'nivel'
          ? { anio, mes, valor: numero }
          : { anio, mes, variacion: numero }
      );
      setValorIndice('');
      enqueueSnackbar(
        modoIndice === 'nivel' ? 'Índice guardado' : 'Variación convertida a nivel y guardada',
        { variant: 'success' }
      );
      await cargarIndices();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo guardar el índice'), { variant: 'error' });
    } finally {
      setGuardandoIndice(false);
    }
  };

  const cerrarImportar = () => {
    if (importando) return;
    setOpenImportar(false);
    setImportFileName('');
    setErroresImportacion([]);
  };

  const abrirImportar = () => {
    setImportFileName('');
    setErroresImportacion([]);
    setOpenImportar(true);
  };

  const descargarPlantilla = () => {
    const aoa = [
      ['Año', 'Mes', 'Índice', 'Variación %'],
      [2025, 12, 100, ''],
      [2026, 1, '', 2.5],
      [2026, 2, 105.1, ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Índices');
    XLSX.writeFile(wb, 'plantilla_indices_ipc.xlsx');
  };

  const importarExcel = async (archivo: File) => {
    try {
      setImportando(true);
      setErroresImportacion([]);
      setImportFileName(archivo.name);
      const buffer = await archivo.arrayBuffer();
      const libro = XLSX.read(buffer, { type: 'array', cellDates: true });
      const hoja = libro.Sheets[libro.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(hoja, { header: 1, raw: true, defval: null }) as unknown[][];
      const { filas, errores } = parsearIndicesExcel(matrix);
      if (errores.length > 0) {
        setErroresImportacion(errores);
        return;
      }
      const resultado = await ajusteInflacionService.importarIndices(filas);
      enqueueSnackbar(`Se importaron ${resultado.guardados} índices`, { variant: 'success' });
      await cargarIndices();
      setOpenImportar(false);
      setImportFileName('');
      setErroresImportacion([]);
    } catch (error) {
      setErroresImportacion(erroresDe(error));
    } finally {
      setImportando(false);
    }
  };

  const editarIndice = (row: IndiceInflacion) => {
    setAnioIndice(String(row.anio));
    setMesIndice(String(row.mes));
    setModoIndice('nivel');
    setValorIndice(String(row.valor).replace('.', ','));
  };

  const eliminarIndice = async (anio: number, mes: number) => {
    try {
      await ajusteInflacionService.eliminarIndice(anio, mes);
      await cargarIndices();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo eliminar el índice'), { variant: 'error' });
    }
  };

  const calcular = async () => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá el rango del ejercicio', { variant: 'warning' });
      return;
    }
    try {
      setCalculando(true);
      setErrores([]);
      const data = await ajusteInflacionService.calcular({
        fechaDesde,
        fechaHasta,
        anioReferencia: anioReferencia ? Number(anioReferencia) : null,
        mesReferencia: mesReferencia ? Number(mesReferencia) : null,
      });
      setProceso(data);
      enqueueSnackbar('Cálculo listo', { variant: 'success' });
      await cargarHistorial();
      await cargarPrerrequisitos(fechaDesde, fechaHasta, anioReferencia, mesReferencia);
    } catch (error) {
      setProceso(null);
      setErrores(erroresDe(error));
      await cargarPrerrequisitos(fechaDesde, fechaHasta, anioReferencia, mesReferencia);
    } finally {
      setCalculando(false);
    }
  };

  const generar = async () => {
    if (!proceso) return;
    try {
      setGenerando(true);
      setErrores([]);
      const data = await ajusteInflacionService.generar(proceso.id);
      setProceso(data);
      enqueueSnackbar(
        data.nroAsiento ? `Asiento ${data.nroAsiento} generado` : 'Asiento generado',
        { variant: 'success' }
      );
      await cargarHistorial();
    } catch (error) {
      setErrores(erroresDe(error));
    } finally {
      setGenerando(false);
    }
  };

  const exportar = () => {
    if (!proceso) return;
    const aoa: (string | number)[][] = [
      ['Cuenta', 'Nombre', 'Clase', 'Saldo inicial', 'Coeficiente', 'Ajuste inicial', 'Movimientos', 'Ajuste movimientos', 'Ajuste total', 'Saldo histórico', 'Saldo reexpresado'],
      ...proceso.lineas.map((l) => [
        l.nroCuenta,
        l.nomCuenta,
        l.claseDescripcion,
        l.saldoInicial,
        l.coeficienteInicial,
        l.ajusteInicial,
        l.netoMovimientos,
        l.ajusteMovimientos,
        l.ajusteTotal,
        l.saldoHistorico,
        l.saldoReexpresado,
      ]),
      [],
      [textoRecpam(proceso.ajusteRecpam)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ajuste inflación');
    XLSX.writeFile(wb, `ajuste_inflacion_${toInputDate(proceso.fechaDesde)}_${toInputDate(proceso.fechaHasta)}.xlsx`);
  };

  const filtroNroNorm = filtroNroCuenta.trim();
  const filtroNombreNorm = normalizarClave(filtroNombreCuenta.trim());
  const pendientesFiltradas = pendientes.filter((c) => {
    if (filtroNroNorm && !String(c.nroCuenta).includes(filtroNroNorm)) return false;
    if (filtroNombreNorm) {
      const hay = normalizarClave(`${c.nomCuenta || ''} ${c.motivo || ''}`).includes(filtroNombreNorm);
      if (!hay) return false;
    }
    return true;
  });
  const clasificablesEnVista = pendientesFiltradas.filter((c) => c.permiteClasificar);
  const seleccionEnVista = seleccionPendientes.filter((nro) =>
    clasificablesEnVista.some((c) => c.nroCuenta === nro)
  );

  const togglePendiente = (nroCuenta: number, permite: boolean) => {
    if (!permite) return;
    setSeleccionPendientes((prev) =>
      prev.includes(nroCuenta) ? prev.filter((n) => n !== nroCuenta) : [...prev, nroCuenta]
    );
  };

  const toggleTodasPendientes = () => {
    const visibles = clasificablesEnVista.map((c) => c.nroCuenta);
    const todasVisiblesMarcadas =
      visibles.length > 0 && visibles.every((nro) => seleccionPendientes.includes(nro));
    if (todasVisiblesMarcadas) {
      setSeleccionPendientes((prev) => prev.filter((nro) => !visibles.includes(nro)));
      return;
    }
    setSeleccionPendientes((prev) => Array.from(new Set([...prev, ...visibles])));
  };

  const clasificarMasivo = async () => {
    if (seleccionPendientes.length === 0) {
      enqueueSnackbar('Seleccioná al menos una cuenta', { variant: 'warning' });
      return;
    }
    if (!CLASES_AJUSTE.some((c) => c.value === claseMasiva)) {
      enqueueSnackbar('Elegí una clase válida', { variant: 'warning' });
      return;
    }
    try {
      setClasificandoMasivo(true);
      const resultado = await ajusteInflacionService.clasificarMasivo({
        nrosCuenta: seleccionPendientes,
        clase: claseMasiva,
      });
      enqueueSnackbar(`Se clasificaron ${resultado.actualizados} cuentas`, { variant: 'success' });
      setSeleccionPendientes([]);
      await cargarPrerrequisitos(fechaDesde, fechaHasta, anioReferencia, mesReferencia);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo clasificar'), { variant: 'error' });
    } finally {
      setClasificandoMasivo(false);
    }
  };

  const abrirHistorial = async (id: number) => {
    try {
      setErrores([]);
      const data = await ajusteInflacionService.getProceso(id);
      setProceso(data);
      setFechaDesde(toInputDate(data.fechaDesde));
      setFechaHasta(toInputDate(data.fechaHasta));
      setAnioReferencia(String(data.anioIndiceReferencia));
      setMesReferencia(String(data.mesIndiceReferencia));
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo abrir el cálculo'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
            Ajuste por inflación
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reexpresión a moneda de cierre y asiento de RECPAM. Clasificá cada cuenta en el plan de cuentas.
            {cuentaRecpam ? ` Cuenta de resultado: ${cuentaRecpam}.` : ' Falta la cuenta de resultado por exposición a la inflación en la estrategia 1.'}
          </Typography>

          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
            <Tab label="Índices" sx={{ textTransform: 'none' }} />
            <Tab label="Proceso" sx={{ textTransform: 'none' }} />
            <Tab
              label={totalPrerrequisitos > 0 ? `Prerrequisitos (${totalPrerrequisitos})` : 'Prerrequisitos'}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
                <TextField label="Año" type="number" size="small" value={anioIndice} onChange={(e) => setAnioIndice(e.target.value)} sx={{ width: 120 }} />
                <TextField label="Mes" type="number" size="small" value={mesIndice} onChange={(e) => setMesIndice(e.target.value)} inputProps={{ min: 1, max: 12 }} sx={{ width: 100 }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="modo-indice-label">Forma</InputLabel>
                  <Select
                    labelId="modo-indice-label"
                    label="Forma"
                    value={modoIndice}
                    onChange={(e) => {
                      setModoIndice(e.target.value as ModoIndice);
                      setValorIndice('');
                    }}
                  >
                    <MenuItem value="nivel">Nivel IPC</MenuItem>
                    <MenuItem value="variacion">Variación %</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={modoIndice === 'nivel' ? 'Nivel IPC' : 'Variación %'}
                  size="small"
                  value={valorIndice}
                  onChange={(e) => setValorIndice(e.target.value)}
                  sx={{ width: 180 }}
                  helperText={modoIndice === 'variacion' ? 'Ej. 2,5 (no 0,025)' : undefined}
                />
                <Button variant="contained" onClick={() => void guardarIndice()} disabled={guardandoIndice} sx={{ textTransform: 'none' }}>
                  {guardandoIndice ? <CircularProgress size={22} /> : 'Guardar'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={abrirImportar}
                  disabled={importando}
                  sx={{ textTransform: 'none' }}
                >
                  Importar Excel
                </Button>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {modoIndice === 'nivel'
                    ? 'Podés cargar el nivel absoluto o la variación %; el sistema guarda siempre el nivel.'
                    : 'La variación se convierte con el nivel del mes anterior ya cargado.'}
                </Typography>
                <Tooltip title="Nivel vs variación">
                  <IconButton
                    aria-label="Nivel vs variación del índice"
                    size="small"
                    color="primary"
                    onClick={() => setAyudaIndiceOpen(true)}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Año</TableCell>
                      <TableCell>Mes</TableCell>
                      <TableCell align="right">Índice</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cargandoIndices ? (
                      <TableRow><TableCell colSpan={4}><CircularProgress size={22} /></TableCell></TableRow>
                    ) : indices.length === 0 ? (
                      <TableRow><TableCell colSpan={4}>No hay índices cargados.</TableCell></TableRow>
                    ) : indices.map((row) => (
                      <TableRow key={`${row.anio}-${row.mes}`}>
                        <TableCell>{row.anio}</TableCell>
                        <TableCell>{String(row.mes).padStart(2, '0')}</TableCell>
                        <TableCell align="right">{coef.format(row.valor)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`Editar índice ${String(row.mes).padStart(2, '0')}/${row.anio}`}
                              onClick={() => editarIndice(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Eliminar índice ${String(row.mes).padStart(2, '0')}/${row.anio}`}
                              onClick={() => void eliminarIndice(row.anio, row.mes)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField label="Desde" type="date" size="small" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Hasta" type="date" size="small" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Año referencia" type="number" size="small" value={anioReferencia} onChange={(e) => setAnioReferencia(e.target.value)} sx={{ width: 140 }} helperText="Saldo inicial" />
                <TextField label="Mes referencia" type="number" size="small" value={mesReferencia} onChange={(e) => setMesReferencia(e.target.value)} sx={{ width: 140 }} inputProps={{ min: 1, max: 12 }} helperText="Saldo inicial" />
                <Button variant="contained" onClick={() => void calcular()} disabled={calculando} sx={{ textTransform: 'none' }}>
                  {calculando ? <CircularProgress size={22} color="inherit" /> : 'Calcular'}
                </Button>
              </Stack>

              {errores.length > 0 && (
                <Alert severity="warning">
                  {errores.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </Alert>
              )}

              {totalPrerrequisitos > 0 && (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={() => setTab(2)} sx={{ textTransform: 'none' }}>
                      Ver prerrequisitos
                    </Button>
                  }
                >
                  Hay {totalPrerrequisitos} prerrequisito{totalPrerrequisitos === 1 ? '' : 's'} pendiente
                  {totalPrerrequisitos === 1 ? '' : 's'} (cuentas sin clasificar, mal clasificadas u otros). Revisalos antes de calcular.
                </Alert>
              )}

              {proceso && (
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {textoRecpam(proceso.ajusteRecpam)} · {proceso.estado}
                      {proceso.nroAsiento ? ` · Asiento ${proceso.nroAsiento}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" onClick={exportar} sx={{ textTransform: 'none' }}>Excel</Button>
                      <Button variant="contained" onClick={() => void generar()} disabled={generando} sx={{ textTransform: 'none' }}>
                        {generando ? <CircularProgress size={22} color="inherit" /> : 'Generar asiento'}
                      </Button>
                      {proceso.nroAsiento ? (
                        <Button variant="text" onClick={() => navigate(`/contabilidad/asientos-diario/${proceso.nroAsiento}`)} sx={{ textTransform: 'none' }}>
                          Ver asiento
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cuenta</TableCell>
                          <TableCell>Clase</TableCell>
                          <TableCell align="right">Saldo inicial</TableCell>
                          <TableCell align="right">Ajuste inicial</TableCell>
                          <TableCell align="right">Movimientos</TableCell>
                          <TableCell align="right">Ajuste mov.</TableCell>
                          <TableCell align="right">Ajuste total</TableCell>
                          <TableCell align="right">Saldo reexpresado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {proceso.lineas.length === 0 ? (
                          <TableRow><TableCell colSpan={8}>No hay cuentas ajustables con saldo en el rango.</TableCell></TableRow>
                        ) : proceso.lineas.map((linea: AjusteInflacionLinea) => (
                          <TableRow key={linea.nroCuenta}>
                            <TableCell>{linea.nroCuenta} {(linea.nomCuenta || '').trim()}</TableCell>
                            <TableCell>{linea.claseDescripcion}</TableCell>
                            <TableCell align="right">{money.format(linea.saldoInicial)}</TableCell>
                            <TableCell align="right">{money.format(linea.ajusteInicial)}</TableCell>
                            <TableCell align="right">{money.format(linea.netoMovimientos)}</TableCell>
                            <TableCell align="right">{money.format(linea.ajusteMovimientos)}</TableCell>
                            <TableCell align="right">{money.format(linea.ajusteTotal)}</TableCell>
                            <TableCell align="right">{money.format(linea.saldoReexpresado)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}

              {historial.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Cálculos anteriores</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Rango</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">RECPAM</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historial.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{formatFecha(row.fechaAlta)}</TableCell>
                            <TableCell>{formatFecha(row.fechaDesde)} – {formatFecha(row.fechaHasta)}</TableCell>
                            <TableCell>{row.estado}{row.nroAsiento ? ` · ${row.nroAsiento}` : ''}</TableCell>
                            <TableCell align="right">{money.format(row.ajusteRecpam)}</TableCell>
                            <TableCell align="right">
                              <Button size="small" onClick={() => void abrirHistorial(row.id)} sx={{ textTransform: 'none' }}>Abrir</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}
            </Stack>
          )}

          {tab === 2 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Condiciones a resolver antes de calcular: cuentas sin clasificar o mal clasificadas, índices faltantes,
                estrategia sin RECPAM u otros bloqueos. Las filas clasificables se pueden corregir en masa.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
                <TextField
                  label="Desde"
                  type="date"
                  size="small"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Hasta"
                  type="date"
                  size="small"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  onClick={() => void cargarPrerrequisitos(fechaDesde, fechaHasta, anioReferencia, mesReferencia)}
                  disabled={cargandoPendientes || !fechaDesde || !fechaHasta}
                  sx={{ textTransform: 'none' }}
                >
                  {cargandoPendientes ? <CircularProgress size={22} /> : 'Actualizar'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/contabilidad/plan-cuentas')}
                  sx={{ textTransform: 'none' }}
                >
                  Plan de cuentas
                </Button>
                <Button
                  variant="text"
                  onClick={() => setTab(0)}
                  sx={{ textTransform: 'none' }}
                >
                  Ir a Índices
                </Button>
              </Stack>

              {alertasPrerreq.length > 0 && (
                <Stack spacing={1}>
                  {alertasPrerreq.map((alerta) => (
                    <Alert key={`${alerta.codigo}-${alerta.mensaje}`} severity="warning">
                      {alerta.mensaje}
                    </Alert>
                  ))}
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
                <TextField
                  label="Nº cuenta"
                  size="small"
                  value={filtroNroCuenta}
                  onChange={(e) => setFiltroNroCuenta(e.target.value)}
                  placeholder="Contiene…"
                  sx={{ width: { xs: '100%', sm: 140 } }}
                />
                <TextField
                  label="Nombre / motivo"
                  size="small"
                  value={filtroNombreCuenta}
                  onChange={(e) => setFiltroNombreCuenta(e.target.value)}
                  placeholder="Contiene…"
                  sx={{ width: { xs: '100%', sm: 260 } }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="clase-masiva-label">Clase a aplicar</InputLabel>
                  <Select
                    labelId="clase-masiva-label"
                    label="Clase a aplicar"
                    value={claseMasiva}
                    onChange={(e) => setClaseMasiva(e.target.value)}
                  >
                    {CLASES_AJUSTE.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={() => void clasificarMasivo()}
                  disabled={clasificandoMasivo || seleccionPendientes.length === 0}
                  sx={{ textTransform: 'none' }}
                >
                  {clasificandoMasivo
                    ? <CircularProgress size={22} color="inherit" />
                    : `Aplicar a ${seleccionPendientes.length || 0} seleccionada${seleccionPendientes.length === 1 ? '' : 's'}`}
                </Button>
              </Stack>
              {(filtroNroNorm || filtroNombreNorm) && pendientes.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Mostrando {pendientesFiltradas.length} de {pendientes.length}
                  {seleccionPendientes.length > 0
                    ? ` · ${seleccionPendientes.length} seleccionada${seleccionPendientes.length === 1 ? '' : 's'}`
                    : ''}
                </Typography>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          indeterminate={
                            seleccionEnVista.length > 0 && seleccionEnVista.length < clasificablesEnVista.length
                          }
                          checked={
                            clasificablesEnVista.length > 0
                            && seleccionEnVista.length === clasificablesEnVista.length
                          }
                          onChange={toggleTodasPendientes}
                          disabled={clasificablesEnVista.length === 0 || cargandoPendientes}
                          inputProps={{ 'aria-label': 'Seleccionar todas las cuentas clasificables filtradas' }}
                        />
                      </TableCell>
                      <TableCell>Cuenta</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Clase actual</TableCell>
                      <TableCell>Problema</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cargandoPendientes ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    ) : !fechaDesde || !fechaHasta ? (
                      <TableRow>
                        <TableCell colSpan={5}>Indicá el rango de fechas del ejercicio.</TableCell>
                      </TableRow>
                    ) : alertasPrerreq.length === 0 && pendientes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>No hay prerrequisitos pendientes en el rango.</TableCell>
                      </TableRow>
                    ) : pendientes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>No hay problemas de cuentas; revisá las alertas de arriba.</TableCell>
                      </TableRow>
                    ) : pendientesFiltradas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>Ninguna cuenta coincide con el filtro.</TableCell>
                      </TableRow>
                    ) : (
                      pendientesFiltradas.map((c) => {
                        const checked = seleccionPendientes.includes(c.nroCuenta);
                        const permite = c.permiteClasificar;
                        return (
                          <TableRow
                            key={`${c.nroCuenta}-${c.codigo}`}
                            hover={permite}
                            selected={checked}
                            onClick={() => togglePendiente(c.nroCuenta, permite)}
                            sx={{ cursor: permite ? 'pointer' : 'default' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={!permite}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => togglePendiente(c.nroCuenta, permite)}
                                inputProps={{ 'aria-label': `Seleccionar cuenta ${c.nroCuenta}` }}
                              />
                            </TableCell>
                            <TableCell>{c.nroCuenta}</TableCell>
                            <TableCell>{(c.nomCuenta || '').trim() || '—'}</TableCell>
                            <TableCell>{(c.claseActual || '').trim() || '—'}</TableCell>
                            <TableCell>{c.motivo}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </CardContent>
      </Card>
      <Dialog open={openImportar} onClose={cerrarImportar} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Índices IPC</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Plantilla con Año, Mes, Índice y Variación %. Completá Índice o Variación en cada fila (si hay ambos, gana el Índice).
              </Typography>
              <Button
                variant="contained"
                color="info"
                fullWidth
                sx={{ fontWeight: 700, py: 1.2, mb: 1 }}
                startIcon={<CloudDownloadIcon />}
                onClick={descargarPlantilla}
              >
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Seleccioná el Excel. Las filas con variación % se convierten al nivel usando el mes anterior (del archivo o ya guardado).
              </Typography>
              <label htmlFor="excel-upload-indices-ipc">
                <input
                  id="excel-upload-indices-ipc"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  disabled={importando}
                  onChange={(e) => {
                    const archivo = e.target.files?.[0];
                    e.target.value = '';
                    if (archivo) void importarExcel(archivo);
                  }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  startIcon={importando ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                  component="span"
                  disabled={importando}
                  sx={{ fontWeight: 700, py: 1.2 }}
                >
                  {importando ? 'Importando...' : 'Seleccionar Archivo'}
                </Button>
              </label>
              {importFileName && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="body2" fontWeight={600}>
                    {importFileName}
                  </Typography>
                </Box>
              )}
              {erroresImportacion.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {erroresImportacion.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarImportar} color="inherit" disabled={importando}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      <IndiceIpcAyudaDialog open={ayudaIndiceOpen} onClose={() => setAyudaIndiceOpen(false)} />
    </Box>
  );
};

export default AjusteInflacion;
